/**
 * src/components/agent/BugReportPanel.jsx — TICKET-1479. The chat's bug-report mode.
 *
 * Mounted by AgentShell in place of the normal message list + composer when the user taps
 * "Report a problem". The AI interviewer asks one short question per turn (server side)
 * until the report is complete; the user can add screenshots at any time and the AI may ask
 * for one. When the report closes, the user sees its reference and their own reports' status.
 *
 * TICKET-1484: `kind` = bug (default) | idea | help. Same engine and panel; idea has its own
 * title and closing line; help is a Q&A (no screenshot, never "sent") that can convert to an idea.
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ImagePlus, Send } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import useBugReportChat, { bugText } from '../../lib/hooks/useBugReportChat';
import { IMAGE_TYPES } from '../../lib/api/bugReports';

const btn = {
  minHeight: 44, minWidth: 44, padding: '8px 12px', border: 'none', borderRadius: 10,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  fontSize: 'var(--fs-13)', fontWeight: 600, cursor: 'pointer',
};
const primary = { ...btn, background: 'var(--accent)', color: 'var(--on-accent)' };
const quiet = { ...btn, background: 'var(--glass)', color: 'var(--t1)', outline: '1px solid var(--border)' };
const off = { opacity: 0.5, cursor: 'default' };

export default function BugReportPanel({ route, onExit, api, kind = 'bug' }) {
  const { t, locale } = useT();
  const lang = locale === 'en' ? 'en' : 'es';
  const bug = useBugReportChat({ language: lang, api, kind });
  const mode = bug.kind;
  const [input, setInput] = useState('');
  const fileRef = useRef(null);
  const tx = (k) => bugText(t, lang, k);
  const { start, loadMine, done } = bug;

  useEffect(() => { start(route); }, [start, route]);
  useEffect(() => { if (done) loadMine(); }, [done, loadMine]);

  const busy = !!bug.busy;
  const canType = !!bug.report && !bug.done && !busy;
  const submit = () => {
    if (!canType || !input.trim()) return;
    bug.send(input);
    setInput('');
  };

  return (
    <div data-testid="bug-report-panel" data-kind={mode} style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px' }}>
        <button type="button" data-testid="bug-report-exit" onClick={onExit} style={quiet} aria-label={tx('exit')}>
          <ArrowLeft size={18} /> {tx('exit')}
        </button>
        <div style={{ flex: 1, minWidth: 0, fontWeight: 700, color: 'var(--t1)', fontSize: 'var(--fs-14)' }}>{tx(mode === 'idea' ? 'titleIdea' : mode === 'help' ? 'titleHelp' : 'title')}</div>
        {bug.report?.reference && mode !== 'help' && (
          <span data-testid="bug-report-reference" style={{ fontSize: 'var(--fs-12)', color: 'var(--t2)' }}>
            {tx('reference')}: {bug.report.reference}
          </span>
        )}
      </div>

      <div data-testid="bug-report-messages" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {bug.messages.map((m, i) => (
          <div
            key={i}
            data-testid={`bug-report-msg-${m.role}`}
            style={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%',
              padding: '8px 12px', borderRadius: 12, fontSize: 'var(--fs-14)', lineHeight: 1.4,
              background: m.role === 'user' ? 'var(--navy-surface)' : 'var(--card)',
              color: m.role === 'user' ? 'var(--on-navy)' : 'var(--t1)',
              outline: m.role === 'user' ? 'none' : '1px solid var(--border)',
            }}
          >
            {m.image ? <span><ImagePlus size={14} /> {m.image}</span> : m.text}
          </div>
        ))}
        {busy && <div data-testid="bug-report-busy" style={{ color: 'var(--t2)', fontSize: 'var(--fs-13)' }}>{tx(bug.busy)}</div>}
        {bug.wantsScreenshot && !bug.done && (
          <div data-testid="bug-report-ask-screenshot" style={{ color: 'var(--info)', background: 'var(--info-tint)', padding: '8px 12px', borderRadius: 10, fontSize: 'var(--fs-13)' }}>
            {tx('askScreenshot')}
          </div>
        )}
        {bug.offerIdea && !bug.done && (
          <button type="button" data-testid="bug-report-convert-idea" onClick={bug.convertToIdea} disabled={busy} style={{ ...primary, alignSelf: 'flex-start', ...(busy ? off : {}) }}>
            {tx('convertIdea')}
          </button>
        )}
        {bug.error && (
          <div data-testid="bug-report-error" role="alert" style={{ color: 'var(--bad)', background: 'var(--bad-tint)', padding: '8px 12px', borderRadius: 10, fontSize: 'var(--fs-13)' }}>
            {tx(bug.error)}
          </div>
        )}
        {bug.done && (
          <div data-testid="bug-report-done" style={{ color: 'var(--ok)', background: 'var(--ok-tint)', padding: '8px 12px', borderRadius: 10, fontSize: 'var(--fs-13)' }}>
            {tx(mode === 'idea' ? 'doneIdea' : 'done')}: {bug.report.reference}
          </div>
        )}
        {bug.done && bug.myReports.length > 0 && (
          <div data-testid="bug-report-mine" style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-13)', color: 'var(--t1)', marginBottom: 4 }}>{tx('myReports')}</div>
            {bug.myReports.map((r) => (
              <div key={r.id} data-testid="bug-report-mine-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 'var(--fs-13)', color: 'var(--t2)', padding: '4px 0' }}>
                <span>{r.reference}</span>
                <span>{tx(`status_${r.display_status}`)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '8px 12px calc(8px + env(safe-area-inset-bottom, 0px))', alignItems: 'flex-end' }}>
        <input
          ref={fileRef}
          type="file"
          accept={IMAGE_TYPES.join(',')}
          data-testid="bug-report-file-input"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) bug.sendImage(f);
          }}
        />
        {mode !== 'help' && <button
          type="button"
          data-testid="bug-report-attach"
          aria-label={tx('attach')}
          title={tx('attach')}
          disabled={!canType}
          onClick={() => fileRef.current?.click()}
          style={{ ...(bug.wantsScreenshot ? primary : quiet), ...(canType ? {} : off) }}
        >
          <ImagePlus size={18} />
        </button>}
        <textarea
          data-testid="bug-report-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder={tx('placeholder')}
          aria-label={tx('placeholder')}
          disabled={!canType}
          rows={1}
          style={{
            flex: 1, minHeight: 44, borderRadius: 8, fontSize: 'var(--fs-14)', outline: 'none', resize: 'none',
            padding: '10px 12px', border: 'none', background: 'var(--card)', color: 'var(--t1)',
            boxShadow: 'inset 0 0 0 1px var(--border)',
          }}
        />
        <button
          type="button"
          data-testid="bug-report-send"
          aria-label={tx('send')}
          disabled={!canType || !input.trim()}
          onClick={submit}
          style={{ ...primary, ...(canType && input.trim() ? {} : off) }}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}
